import React, { FC, Ref, useCallback, useEffect, useMemo, useRef } from "react"
import { observer } from "mobx-react-lite"
import {
  Appearance,
  Dimensions,
  ImageStyle,
  InteractionManager,
  Platform,
  TextStyle,
  View,
  ViewStyle,
} from "react-native"
import AppIntroSlider from "react-native-app-intro-slider"

import { AutoImage, Button, PickerLanguage, Screen, Text } from "../../../components"
import { useStores } from "../../../models"
import { color, nativeBaseTheme, images } from "../../../theme"
import {
  AppStackInitialSync,
  // AppStackInitialSync,
  navigate,
  resetPersistence,
  resetRoot,
  // resetRoot,
} from "../../../navigators"
import config from "../../../utils/config"
import _ from "lodash"
import I18n from "i18n-js"
import JalaAnalytic from 
"../../../utils/jala-analytic"
import { useFocusEffect } from "@react-navigation/native"
import codePushCheck from "../../../utils/code-push"
import inAppUpdateCheck from "../../../utils/in-app-update"

const ROOT: ViewStyle = {
  flexGrow: 1,
}

const TOP_BAR_TITLE_CONTAINER: ViewStyle = {
  marginLeft: 16,
  justifyContent: "center",
}

const TOP_BAR_CONTAINER: ViewStyle = {
  marginRight: 16,
  justifyContent: "center",
}

const INTRO_SLIDER: ViewStyle = {
  alignItems: Platform.OS === 'web' ? "center" : undefined,
  justifyContent: "center",
}

const TOP_CONTAINER: ViewStyle = {
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 80
}

const ONBOARD: ImageStyle = {
  width: Dimensions.get("window").width * 280 / 320,
  height: Dimensions.get("window").width * 280 / 320,
  resizeMode: "contain",
}

const ONBOARD_TEXT: TextStyle = {
  marginHorizontal: 16,
  marginTop: 20,
  textAlign: "center"
}

const LOGIN_BUTTON: ViewStyle = {
  marginHorizontal: 16,
}

const REGISTER_BUTTON: ViewStyle = {
  marginHorizontal: 16,
  marginTop: 16,
}

const DEMO: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  marginTop: 7,
  height: 64,
}

const DEMO_BUTTON: ViewStyle = {
  marginLeft: 5,
}

const JALA_LOGO: ImageStyle = {
  height: 54,
  width:130,
  resizeMode: 'contain'
}


const onboardImages = [
  {
    source: require('./onboard1.png'),
    title: "onboardScreen.onboard1"
  },
  {
    source: require('./onboard2.png'),
    title: "onboardScreen.onboard2"
  },
  {
    source: require('./onboard3.png'),
    title: "onboardScreen.onboard3"
  },
  {
    source: require('./onboard4.png'),
    title: "onboardScreen.onboard4"
  },
  {
    source: require('./onboard5.png'),
    title: "onboardScreen.onboard5"
  },
  {
    source: require('./onboard6.png'),
    title: "onboardScreen.onboard6"
  },
];

export const OnboardTitle: FC = observer(function OnboardTitle(props) {
  return <View style={TOP_BAR_TITLE_CONTAINER}>
    <AutoImage source={images.logo_blue} style={JALA_LOGO} />
  </View>
})

export const OnboardLanguagePicker: FC = observer(function OnboardLanguagePicker(props) {
  return (
    <View style={TOP_BAR_CONTAINER}>
      <PickerLanguage colorScheme="light" />
    </View>
  )
})

export const OnboardScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function OnboardScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;

  // Pull in one of our MST stores
  const {
    appStore,
    loginStore,
    tokenStore,
    // tokenStore: { token }
  } = useStores()

  const sliderRef: Ref<AppIntroSlider> = useRef();
  const intervalId = useRef<NodeJS.Timer>()

  const activeDotStyle = useMemo(() => ({
    backgroundColor: colorScheme === "light" ? color.primary : nativeBaseTheme.colors.light[200]
  }), [colorScheme])

  const dotStyle = useMemo(() => ({
    backgroundColor: colorScheme === "light" ? color.palette.blueJalaLight : nativeBaseTheme.colors.dark[200]
  }), [colorScheme])

  const stopInterval = useCallback(() => {
    if (intervalId.current) {
      clearInterval(intervalId.current)
      intervalId.current = null;
    }
  }, [])

  const startInterval = useCallback(() => {
    if (intervalId.current) stopInterval()
    intervalId.current = setInterval(() => {
      if (!sliderRef.current) return;
      sliderRef.current.goToSlide(
        (sliderRef.current.state.activeIndex + 1) % onboardImages.length,
        true
      );
    }, 3000)
    return stopInterval;
  }, [stopInterval])

  useEffect(() => {
    tokenStore.set('demo', false)
    appStore.set('locale', I18n.locale)
    loginStore.initSocialLoginEnabled()
  }, [appStore, loginStore, tokenStore])

  useEffect(() => {
    return startInterval()
  }, [startInterval]);

  useFocusEffect(useCallback(() => {
    InteractionManager.runAfterInteractions(() => {
      codePushCheck().catch();
      inAppUpdateCheck().catch()
    })
  }, []))

  const renderItem = useCallback((info) => (
    <View style={TOP_CONTAINER}>
      <AutoImage source={info.item.source} style={ONBOARD} />
      <Text
        tx={info.item.title}
        preset="display2"
        colorScheme={colorScheme}
        color={colorScheme === "light" ? color.palette.blueJalaDark : undefined}
        numberOfLines={2}
        style={ONBOARD_TEXT}
      />
    </View>
  ), [colorScheme])

  const onMomentumScrollEnd = useMemo(() => _.debounce((event) => {
    if (event.nativeEvent) sliderRef.current._onMomentumScrollEnd(event)
    startInterval();
  }, 100, { leading: false, trailing: true }), [startInterval]);

  const onScrollBeginDrag = useMemo(() => _.debounce(() => {
    stopInterval()
  }, 100, { leading: true, trailing: false }), [stopInterval]);

  const onScroll = useCallback((event) => {
    onScrollBeginDrag()
    onMomentumScrollEnd(event)
  }, [onMomentumScrollEnd, onScrollBeginDrag])

  const keyExtractor = useCallback((item, index) => `slider-${index}`, []);

  const onPressLogin = useCallback(() => {
    const { BASE_URL, OAUTH2_URL, CLIENT_ID } = config()

    if (Platform.OS === 'web') {
      const url = new URL(`${OAUTH2_URL}/authorize`);
      const searchParams = new URLSearchParams()
      searchParams.set('client_id', CLIENT_ID);
      searchParams.set('redirect_uri', `${window.location.origin}/`);
      searchParams.set('response_type', 'token');
      searchParams.set('scope', '*');
      url.search = searchParams.toString();

      const urlLogout = new URL(`${BASE_URL}/logout`);
      const searchParamsLogout = new URLSearchParams()
      searchParamsLogout.set('redirect', url.toString());
      urlLogout.search = searchParamsLogout.toString();

      window.location.href = urlLogout.toString();
      resetPersistence();
    }
    else {
      stopInterval()
      navigate('Login.Stack', 'Login.SocialLogin');
    }
  }, [stopInterval])

  const onPressRegister = useCallback(() => {
    stopInterval();
    navigate('Login.Stack', 'Login.Register')
  }, [stopInterval])

  const onPressDemo = useCallback(() => {
    const { BASE_URL, OAUTH2_URL, CLIENT_ID } = config()

    JalaAnalytic.log('Mobile app: onboard - try demo');

    tokenStore.set('demo', true)
    if (Platform.OS === 'web') {
      const url = new URL(`${OAUTH2_URL}/authorize`);
      const searchParams = new URLSearchParams()
      searchParams.set('client_id', CLIENT_ID);
      searchParams.set('redirect_uri', `${window.location.origin}/`);
      searchParams.set('response_type', 'token');
      searchParams.set('scope', '*');
      url.search = searchParams.toString();

      const urlDemo = new URL(`${BASE_URL}/demo`)
      const searchParamsDemo = new URLSearchParams()
      searchParamsDemo.set('redirect', url.toString());
      urlDemo.search = searchParamsDemo.toString();

      const urlLogout = new URL(`${BASE_URL}/logout`);
      const searchParamsLogout = new URLSearchParams()
      searchParamsLogout.set('redirect', urlDemo.toString());
      urlLogout.search = searchParamsLogout.toString();

      window.location.href = urlLogout.toString()
      resetPersistence();
    }
    else {
      tokenStore.requestToken({ username: 'demo@jala.tech', password: 'password' })
        .then(() => {
          resetPersistence();
          resetRoot(AppStackInitialSync)
        });
    }
  }, [tokenStore])

  return (
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      hideNavigationBar={true}
    >
      <AppIntroSlider
        ref={sliderRef}
        renderItem={renderItem}
        data={onboardImages}
        activeDotStyle={activeDotStyle}
        dotStyle={dotStyle}
        contentContainerStyle={INTRO_SLIDER}
        showPrevButton={false}
        showNextButton={false}
        showDoneButton={false}
        onScroll={Platform.OS === 'web' ? (onScroll) : undefined}
        keyExtractor={keyExtractor}
        onScrollBeginDrag={Platform.OS !== 'web' ? (onScrollBeginDrag) : undefined}
        onScrollEndDrag={startInterval}
      />
      <Button
        testID="jala:id/login-button"
        tx="onboardScreen.login"
        preset="primary"
        colorScheme={colorScheme}
        style={LOGIN_BUTTON}
        onPress={onPressLogin}
      />
      <Button
        testID="jala:id/register-button"
        tx="onboardScreen.register"
        preset="outline"
        colorScheme={colorScheme}
        style={REGISTER_BUTTON}
        onPress={onPressRegister}
      />
      <View style={DEMO}>
        <Text tx="onboardScreen.justTry" preset="body3" colorScheme={colorScheme} />
        <Button
          testID="jala:id/demo-button"
          tx="onboardScreen.tryDemo"
          preset="link"
          colorScheme={colorScheme}
          style={DEMO_BUTTON}
          onPress={onPressDemo}
        />
      </View>
    </Screen>
  )
})
